import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { TourismPackage } from "@/components/tourism-packages/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { 
  ArrowRight, 
  Edit, 
  FileText, 
  Share2, 
  Trash2,
  MapPin,
  Calendar,
  Users,
  Hotel,
  Plane,
  Car,
  Utensils,
  DollarSign,
  CheckCircle,
  XCircle,
  Building,
  Phone,
  Mail,
  Copy,
  Send,
  QrCode,
  Download
} from "lucide-react";
import { toast } from "sonner";
import { getStatusColor, getTypeColor, getPaymentStatusColor } from "@/components/tourism-packages/utils";
import { generatePackagePDF } from "@/utils/pdfGenerator";
import { PackageData } from "./CreatePackage";
import QRCode from "qrcode";

const PackageDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pkg, setPkg] = useState<TourismPackage | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [qrCode, setQrCode] = useState<string>("");
  const packageUrl = `${window.location.origin}/package-details/${id}`;

  useEffect(() => {
    if (id) {
      fetchPackageDetails();
      generateQRCode();
    }
  }, [id]);

  const generateQRCode = async () => {
    try {
      const qr = await QRCode.toDataURL(packageUrl, {
        width: 300,
        margin: 2,
      });
      setQrCode(qr);
    } catch (error) {
      console.error('Error generating QR code:', error);
    }
  };

  const fetchPackageDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('tourism_packages')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setPkg(data as TourismPackage);
    } catch (error) {
      console.error('Error fetching package:', error);
      toast.error("حدث خطأ أثناء تحميل تفاصيل الباقة");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm("هل أنت متأكد من حذف هذه الباقة؟")) return;

    try {
      const { error } = await supabase
        .from('tourism_packages')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success("تم حذف الباقة بنجاح");
      navigate('/tourism-packages');
    } catch (error) {
      console.error('Error deleting package:', error);
      toast.error("حدث خطأ أثناء حذف الباقة");
    }
  };

  const handlePrint = async () => {
    if (!pkg) return;
    
    const packageData: PackageData = {
      packageName: pkg.title,
      destination: pkg.destination,
      duration: pkg.duration,
      notes: '',
      clientType: pkg.type === 'b2b' ? 'b2b' : 'individual',
      clientName: pkg.client_name || '',
      clientEmail: '',
      clientPhone: '',
      clientAddress: '',
      numberOfRooms: 1,
      numberOfNights: parseInt(pkg.duration) || 7,
      mealPlans: ['breakfast'],
      adultsCount: 2,
      childrenCount: 0,
      infantsCount: 0,
      receptionCarsCount: 1,
      tourCarsCount: 1,
      internationalBaggageCount: 1,
      internationalBaggageWeight: 23,
      internationalCabinWeight: 7,
      domesticBaggageCount: 1,
      domesticBaggageWeight: 20,
      domesticCabinWeight: 7,
      citiesPlan: [],
      includesHotels: true,
      includesInternationalFlights: true,
      includesDomesticFlights: false,
      includesTransport: true,
      includesTours: true,
      includesServices: false,
      hotels: [],
      flights: [],
      transport: [],
      tours: [],
      inclusions: [],
      exclusions: [],
      services: [],
      basePrice: pkg.price,
      discount: 0,
      taxes: 0,
      totalPrice: pkg.price,
      paymentTerms: ''
    };

    await generatePackagePDF(packageData, 'template1');
  };

  const handleCopyPackage = async () => {
    if (!pkg) return;
    
    try {
      const { data: newPackage, error } = await supabase
        .from('tourism_packages')
        .insert({
          ...pkg,
          id: undefined,
          package_number: `PKG-${Date.now()}`,
          title: `${pkg.title} (نسخة)`,
          status: 'draft',
          created_at: undefined,
          updated_at: undefined,
        })
        .select()
        .single();

      if (error) throw error;
      toast.success("تم نسخ الباقة بنجاح");
      navigate(`/package-details/${newPackage.id}`);
    } catch (error) {
      console.error('Error copying package:', error);
      toast.error("حدث خطأ أثناء نسخ الباقة");
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(packageUrl);
    toast.success("تم نسخ الرابط");
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(`باقة سياحية: ${pkg?.title}`);
    const body = encodeURIComponent(`
مرحباً،

أود مشاركة هذه الباقة السياحية معك:

${pkg?.title}
الوجهة: ${pkg?.destination}
المدة: ${pkg?.duration}
السعر: ${pkg?.price} ريال

يمكنك الاطلاع على التفاصيل الكاملة من خلال الرابط التالي:
${packageUrl}

مع تحياتي
    `);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleShareWhatsApp = () => {
    const text = encodeURIComponent(`
باقة سياحية مميزة: ${pkg?.title}
📍 ${pkg?.destination}
⏰ ${pkg?.duration}
💰 ${pkg?.price} ريال

للتفاصيل: ${packageUrl}
    `);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  if (loading) {
    return (
      <div className="container mx-auto p-6" dir="rtl">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/4"></div>
          <div className="h-64 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!pkg) {
    return (
      <div className="container mx-auto p-6 text-center" dir="rtl">
        <h2 className="text-2xl font-bold text-gray-900">لم يتم العثور على الباقة</h2>
        <Link to="/tourism-packages">
          <Button className="mt-4">العودة للباقات</Button>
        </Link>
      </div>
    );
  }

  const details = pkg.package_details as any;

  return (
    <div className="container mx-auto p-6 space-y-6" dir="rtl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/tourism-packages">
            <Button variant="outline" size="sm">
              <ArrowRight className="ml-2 h-4 w-4" />
              العودة
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{pkg.title}</h1>
            <p className="text-sm text-gray-500">رقم الباقة: #{pkg.package_number}</p>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={handlePrint}>
            <FileText className="ml-2 h-4 w-4" />
            طباعة PDF
          </Button>
          <Button variant="outline" onClick={() => setShareDialogOpen(true)}>
            <Share2 className="ml-2 h-4 w-4" />
            مشاركة
          </Button>
          <Button variant="outline" onClick={handleCopyPackage}>
            <Copy className="ml-2 h-4 w-4" />
            نسخ الباقة
          </Button>
          <Button variant="outline" onClick={() => navigate(`/create-package?edit=${pkg.id}`)}>
            <Edit className="ml-2 h-4 w-4" />
            تحرير
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="ml-2 h-4 w-4" />
            حذف
          </Button>
        </div>
      </div>

      {/* Status Badges */}
      <div className="flex flex-wrap gap-2">
        <Badge className={getStatusColor(pkg.status)}>
          {pkg.status === 'active' ? 'نشط' : pkg.status === 'draft' ? 'مسودة' : 'منتهي'}
        </Badge>
        <Badge className={getTypeColor(pkg.type)}>
          {pkg.type === 'b2b' ? 'B2B' : 'موظف'}
        </Badge>
        <Badge className={getPaymentStatusColor(pkg.payment_status)}>
          {pkg.payment_status === 'paid' ? 'مدفوع' : pkg.payment_status === 'pending' ? 'قيد الانتظار' : 'غير مدفوع'}
        </Badge>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* معلومات العميل */}
          {details?.clientInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  معلومات العميل
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span className="font-medium">الاسم:</span>
                  <span>{details.clientInfo.name}</span>
                </div>
                {details.clientInfo.email && (
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">البريد:</span>
                    <span>{details.clientInfo.email}</span>
                  </div>
                )}
                {details.clientInfo.phone && (
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">الهاتف:</span>
                    <span>{details.clientInfo.phone}</span>
                  </div>
                )}
                {details.clientInfo.companyName && (
                  <div className="flex items-center gap-2">
                    <Building className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">الشركة:</span>
                    <span>{details.clientInfo.companyName}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* تفاصيل الرحلة */}
          {details?.tripDetails && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5" />
                  تفاصيل الرحلة
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm text-gray-500">الوجهة</span>
                    <p className="font-medium">{details.tripDetails.destination}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">المدة</span>
                    <p className="font-medium">{details.tripDetails.duration}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">عدد الليالي</span>
                    <p className="font-medium">{details.tripDetails.numberOfNights} ليلة</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">عدد الغرف</span>
                    <p className="font-medium">{details.tripDetails.numberOfRooms} غرفة</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">عدد البالغين</span>
                    <p className="font-medium">{details.tripDetails.adultsCount}</p>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500">عدد الأطفال</span>
                    <p className="font-medium">{details.tripDetails.childrenCount}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* المدن والفنادق */}
          {details?.citiesPlan && details.citiesPlan.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Hotel className="h-5 w-5" />
                  المدن والفنادق
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {details.citiesPlan.map((city: any, index: number) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="font-medium mb-2">{city.cityName}</h4>
                      <div className="text-sm text-gray-600 space-y-1">
                        {city.hotelName && <p>الفندق: {city.hotelName}</p>}
                        {city.roomName && <p>نوع الغرفة: {city.roomName}</p>}
                        {city.nightsCount && <p>عدد الليالي: {city.nightsCount}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* الخدمات المشمولة */}
          {details?.inclusions && details.inclusions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  الخدمات المشمولة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {details.inclusions.map((item: any, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                      <span>{item.text || item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* الخدمات غير المشمولة */}
          {details?.exclusions && details.exclusions.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  الخدمات غير المشمولة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {details.exclusions.map((item: any, index: number) => (
                    <li key={index} className="flex items-start gap-2">
                      <XCircle className="h-4 w-4 text-red-600 mt-0.5" />
                      <span>{item.text || item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* ملخص السعر */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                ملخص السعر
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {details?.pricing ? (
                <>
                  <div className="flex justify-between">
                    <span className="text-gray-600">السعر الأساسي</span>
                    <span className="font-medium">{details.pricing.basePrice} ريال</span>
                  </div>
                  {details.pricing.profitAmount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">هامش الربح</span>
                      <span className="font-medium text-green-600">+{details.pricing.profitAmount} ريال</span>
                    </div>
                  )}
                  {details.pricing.discount > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">الخصم</span>
                      <span className="font-medium text-red-600">-{details.pricing.discount} ريال</span>
                    </div>
                  )}
                  {details.pricing.taxes > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">الضرائب</span>
                      <span className="font-medium">+{details.pricing.taxes} ريال</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between text-lg font-bold">
                    <span>الإجمالي</span>
                    <span className="text-green-600">{details.pricing.totalPrice} ريال</span>
                  </div>
                </>
              ) : (
                <div className="flex justify-between text-lg font-bold">
                  <span>السعر الإجمالي</span>
                  <span className="text-green-600">{pkg.price} ريال</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* النقاط المميزة */}
          {pkg.highlights && pkg.highlights.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>النقاط المميزة</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {pkg.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <CheckCircle className="h-4 w-4 text-blue-600 mt-0.5" />
                      <span className="text-sm">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* معلومات إضافية */}
          <Card>
            <CardHeader>
              <CardTitle>معلومات إضافية</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">تاريخ الإنشاء</span>
                <span>{new Date(pkg.created_at).toLocaleDateString('ar-EG')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">آخر تحديث</span>
                <span>{new Date(pkg.updated_at).toLocaleDateString('ar-EG')}</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>مشاركة الباقة</DialogTitle>
            <DialogDescription>
              شارك هذه الباقة مع عملائك بطرق مختلفة
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Copy Link */}
            <div className="flex items-center gap-2">
              <Button onClick={handleCopyLink} className="flex-1" variant="outline">
                <Copy className="ml-2 h-4 w-4" />
                نسخ الرابط
              </Button>
            </div>

            {/* Share via Email */}
            <Button onClick={handleShareEmail} className="w-full" variant="outline">
              <Mail className="ml-2 h-4 w-4" />
              مشاركة عبر البريد الإلكتروني
            </Button>

            {/* Share via WhatsApp */}
            <Button onClick={handleShareWhatsApp} className="w-full bg-green-600 hover:bg-green-700 text-white">
              <Send className="ml-2 h-4 w-4" />
              مشاركة عبر واتساب
            </Button>

            {/* QR Code */}
            {qrCode && (
              <div className="border rounded-lg p-4 text-center">
                <p className="text-sm text-gray-600 mb-3">
                  <QrCode className="inline h-4 w-4 ml-1" />
                  رمز QR للباقة
                </p>
                <img src={qrCode} alt="QR Code" className="mx-auto" />
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = `package-${pkg?.package_number}-qr.png`;
                    link.href = qrCode;
                    link.click();
                  }}
                >
                  <Download className="ml-2 h-4 w-4" />
                  تحميل رمز QR
                </Button>
              </div>
            )}

            {/* URL Display */}
            <div className="bg-gray-50 p-3 rounded text-sm break-all text-gray-700">
              {packageUrl}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PackageDetails;